import React, {
  useEffect,
  useMemo,
  useContext,
  Dispatch,
  SetStateAction,
} from "react";
import { AxiosError } from "axios";
import { QueryKey, useQuery } from "react-query";
import { useParams, useRouteMatch } from "react-router-dom";

import {
  hasPermission,
  hasAnyPermission,
} from "fe-shared/src/helpers/permissions";
import { PermissionLevel } from "fe-shared/src/types/domain";
import { ApiError } from "fe-shared/src/types/common";
import { Loader, NotFoundPage } from "fe-shared/src/components";

import UserContext from "../../contexts/userContext";
import { PERMISSIONS, INVENTORY_ADJUSTMENTS_QUERY_KEY } from "../../consts";
import { InventoryAdjustmentDetails } from "../../types/inventoryAdjustments";

import InventoryAdjustmentsDetailsView from "./components/InventoryAdjustmentsDetailsView";
import * as api from "./modules/api";
import styles from "./InventoryAdjustmentsDetails.module.scss";

interface Props {
  setLastOpenedNumber: Dispatch<SetStateAction<string | null>>;
}

const InventoryAdjustmentsDetails = ({ setLastOpenedNumber }: Props) => {
  const { adjustmentNumber } = useParams<{ adjustmentNumber: string }>();
  const { path } = useRouteMatch();
  const user = useContext(UserContext);

  const canViewDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsDetails,
    level: PermissionLevel.Read,
  });

  const canAssign = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsAssign,
    level: PermissionLevel.Full,
  });

  const canEdit = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsEdit,
    level: PermissionLevel.Full,
  });

  const canInvalidate = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsInvalidate,
    level: PermissionLevel.Full,
  });

  const canApproveFirstLevel = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsApproveFirstLevel,
    level: PermissionLevel.Full,
  });

  const canApproveSecondLevel = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsApproveSecondLevel,
    level: PermissionLevel.Full,
  });

  const hasSidebar = hasAnyPermission(user?.permissions, [
    {
      name: PERMISSIONS.inventoryAdjustmentsListNotes,
      level: PermissionLevel.Read,
    },
  ]);

  const canAddNote = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsListAddNote,
    level: PermissionLevel.Full,
  });

  const canExportNotes = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsListNotesExport,
    level: PermissionLevel.Full,
  });

  const canExportHistory = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsListHistoryExport,
    level: PermissionLevel.Full,
  });

  const returnUrl = useMemo(() => path.replace("/:adjustmentNumber", ""), [
    path,
  ]);

  useEffect(() => {
    if (adjustmentNumber) {
      setLastOpenedNumber(adjustmentNumber);
    }
  }, [adjustmentNumber, setLastOpenedNumber]);

  const inventoryAdjustmentQueryKey: QueryKey = [
    INVENTORY_ADJUSTMENTS_QUERY_KEY,
    adjustmentNumber,
  ];
  const { data: inventoryAdjustment, isError, isLoading, error } = useQuery<
    InventoryAdjustmentDetails,
    AxiosError<ApiError>
  >(inventoryAdjustmentQueryKey, api.getInventoryAdjustment, {
    keepPreviousData: true,
    enabled: canViewDetails,
  });

  const isCurrentUserAssignee =
    inventoryAdjustment?.assignee?.userId === user?.userId;
  const isNotFound = error?.response?.status === 404;

  return (
    <div className={styles.pageContainer}>
      {isLoading && <Loader isBlocking />}
      {!isLoading && !isError && inventoryAdjustment && (
        <InventoryAdjustmentsDetailsView
          returnUrl={returnUrl}
          inventoryAdjustment={inventoryAdjustment}
          canAssign={canAssign}
          hasSidebar={hasSidebar}
          canAddNote={canAddNote}
          canExportNotes={canExportNotes}
          canExportHistory={canExportHistory}
          userEmail={user?.email}
          canEdit={canEdit}
          canInvalidate={canInvalidate}
          canApproveFirstLevel={canApproveFirstLevel}
          canApproveSecondLevel={canApproveSecondLevel}
          isCurrentUserAssignee={isCurrentUserAssignee}
          inventoryAdjustmentQueryKey={inventoryAdjustmentQueryKey}
        />
      )}
      {isError && (
        <NotFoundPage
          returnLink="/inventory-adjustments"
          title={
            isNotFound
              ? "inventoryAdjustment.details.error.notFoundTitle"
              : "errors.somethingWrong"
          }
          descriptions={["inventoryAdjustment.details.error.description"]}
          returnLinkDescription="inventoryAdjustment.details.error.returnLink"
        />
      )}
    </div>
  );
};

export default InventoryAdjustmentsDetails;
